import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

// Full-screen scrolling sheet for admin editors. No scroll-lock library: the overlay itself is the
// scroll container, so wheel, trackpad, touch, scrollbar drag and keyboard all scroll the form.
export const EditorSheet = ({ open, onClose, title, children, footer, testId = "editor-sheet" }) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.setAttribute("data-editor-open", "1");
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.body.removeAttribute("data-editor-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] overflow-y-auto overscroll-contain bg-black/70 [-webkit-overflow-scrolling:touch]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={testId}
    >
      <div className="min-h-full flex items-start justify-center sm:p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div role="dialog" aria-modal="true" className="relative w-full sm:max-w-2xl bg-white sm:rounded-xl shadow-2xl min-h-[100dvh] sm:min-h-0 flex flex-col">
          <div className="sticky top-0 z-10 flex items-start gap-3 bg-white/95 backdrop-blur px-5 sm:px-6 py-4 border-b border-[#E2E0DC] sm:rounded-t-xl">
            <h2 className="text-lg font-semibold leading-snug text-[#1A1A1A] flex-1">{title}</h2>
            <button type="button" onClick={onClose} className="rounded-full p-1.5 text-[#5C5C5C] hover:bg-black/5" aria-label="Close" data-testid={`${testId}-close`}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 px-5 sm:px-6 py-4">{children}</div>
          {footer && (
            <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 bg-white/95 backdrop-blur px-5 sm:px-6 py-3 border-t border-[#E2E0DC] sm:rounded-b-xl">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditorSheet;
